// src/components/legal/TermsConditions.tsx
import Footer from "@/components/shared/Footer";
import { ArrowLeft, Clock, Scale, Shield } from "lucide-react";
import { Link } from "react-router-dom";

export default function TermsConditions() {
  const lastUpdated = "September 2025";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 via-purple-600/20 to-pink-600/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 py-16">
          <Link
            to="/"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors mb-8 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>

          <div className="flex items-center mb-6">
            <Scale className="w-8 h-8 text-blue-400 mr-4" />
            <h1 className="text-4xl lg:text-5xl font-bold text-white">
              Terms & Conditions
            </h1>
          </div>

          <p className="text-xl text-gray-300 max-w-3xl">
            Clear terms for using our Culture Lab platform and services
          </p>
          <p className="text-sm text-gray-400 mt-4">
            Last updated: {lastUpdated}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="bg-slate-800/40 backdrop-blur-sm rounded-xl border border-slate-600/50 p-8 space-y-8">
          {/* Quick Summary */}
          <div className="bg-slate-700/30 rounded-lg p-6 border border-blue-500/20">
            <h2 className="text-xl font-semibold text-blue-400 mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              Quick Summary
            </h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-300">
              <div>
                <h3 className="font-semibold text-white mb-2">
                  What you can do:
                </h3>
                <ul className="space-y-1 text-gray-400">
                  <li>• Use Culture Lab analysis for business purposes</li>
                  <li>• Request strategic consultations</li>
                  <li>• Order MagDrops marketing materials</li>
                  <li>• Access our sub-brand services</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-white mb-2">
                  What you can't do:
                </h3>
                <ul className="space-y-1 text-gray-400">
                  <li>• Share login credentials</li>
                  <li>• Misuse our intellectual property</li>
                  <li>• Spam or abuse our services</li>
                  <li>• Reverse engineer our algorithms</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Terms Sections */}
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              1. Service Agreement
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                By using The Content Group's services including Culture Lab
                analysis, MagDrops marketing materials, and our sub-brand
                services, you agree to these terms. Our services help transform
                corporate spaces into cultural experiences through strategic
                consultation and custom marketing materials.
              </p>
              <p className="mb-4">
                Culture Lab provides property analysis and strategic
                recommendations based on your inputs. Results are advisory and
                should be considered alongside professional property and
                business advice.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              2. User Responsibilities
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <h3 className="text-lg font-medium text-white mb-2">
                Information Accuracy
              </h3>
              <p className="mb-4">
                You're responsible for providing accurate property and business
                information. Inaccurate data may affect the quality of our
                analysis and recommendations.
              </p>

              <h3 className="text-lg font-medium text-white mb-2">
                Appropriate Use
              </h3>
              <p className="mb-4">
                Use our services for legitimate business purposes only. Don't
                attempt to reverse engineer, copy, or misuse our proprietary
                analysis methods or intellectual property.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              3. Intellectual Property
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                Our analysis methods, design templates, and strategic frameworks
                are proprietary to The Content Group. You receive a license to
                use results for your specific property but cannot redistribute
                our methodologies.
              </p>
              <p className="mb-4">
                MagDrops designs and content created for your business become
                your property upon payment, but underlying templates and design
                systems remain ours.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              4. Payment & Refunds
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <h3 className="text-lg font-medium text-white mb-2">
                Culture Lab
              </h3>
              <p className="mb-4">
                Basic analysis is free. Strategic consultations are paid
                services with pricing confirmed before booking. Payment is due
                upon service confirmation.
              </p>

              <h3 className="text-lg font-medium text-white mb-2">
                MagDrops & Sub-brands
              </h3>
              <p className="mb-4">
                Custom marketing materials require 50% deposit with remainder
                due before final delivery. Refunds available if we cannot
                deliver agreed specifications, but not for changes in your
                business requirements.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              5. Limitations & Disclaimers
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                Culture Lab analysis is advisory. We're not liable for business
                decisions based on our recommendations. Always consult
                professional advisors for property, legal, and financial
                decisions.
              </p>
              <p className="mb-4">
                We aim for high availability but cannot guarantee uninterrupted
                service. Our liability is limited to the amount you paid for
                specific services.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              6. Privacy & Data
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                Your data handling is covered by our{" "}
                <Link
                  to="/privacy-policy"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Privacy Policy
                </Link>
                . We use your property information to provide analysis and may
                retain it to improve our services (anonymised).
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">
              7. Changes & Contact
            </h2>
            <div className="prose prose-invert max-w-none text-gray-300">
              <p className="mb-4">
                We may update these terms with 30 days notice for material
                changes. Continued use constitutes acceptance of updated terms.
              </p>
              <p className="mb-4">
                Questions about these terms? Contact us directly through our
                website or hello@thecontentgroup.co
              </p>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-slate-600/50 pt-6">
            <div className="flex items-center justify-between text-sm text-gray-400">
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Last updated: {lastUpdated}
              </div>
              <div className="flex space-x-4">
                <Link
                  to="/privacy-policy"
                  className="hover:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/cookie-policy"
                  className="hover:text-white transition-colors"
                >
                  Cookie Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Your existing Footer */}
      <Footer variant="dark" />
    </div>
  );
}
